import type {
  BarberDayOff,
  BarberDayOn,
  BarberSchedule,
  BarberScheduleWorkday,
} from '@/types/barberSchedule';

import { DateFormat, formatDate } from './dateUtils';

export interface BarberScheduleInfo {
  // Snake_case here matches the actual API response — see
  // getProfessionalsQuery/getBarberByIdOrRouteQuery, which fetch these under
  // the same `days_off`/`days_on` keys via the `include` param.
  days_off?: BarberDayOff[];
  days_on?: BarberDayOn[];
  schedules?: BarberSchedule[];
}

// `selectedDate` is a plain local calendar Date (e.g. new Date(y, m, d)) with
// no embedded timezone info, so local formatting is safe here.
const toDateKey = (date: Date): string => formatDate(date, DateFormat.API_DATE);

// The API sends day-off/day-on/schedule-range dates as UTC-midnight-anchored
// ISO strings representing a pure calendar date (e.g. "2026-08-04T00:00:00.000Z"
// means "August 4, 2026", not a specific instant). Reformatting these through
// the local timezone can shift them onto the adjacent day, so take the date
// portion directly instead of parsing them as instants.
const toApiDateKey = (isoDateString: string): string =>
  isoDateString.slice(0, 10);

/**
 * The single place in the app that converts a JS weekday to the API's.
 *
 * JS `Date#getDay()`: Sunday=0, Monday=1 … Saturday=6
 * API `workday.weekDay`: Monday=1 … Saturday=6, Sunday=7 (never 0)
 *
 * The two agree on Mon–Sat purely by coincidence, so a missing conversion
 * fails *only* on Sundays — which is exactly how FDBK-3653 reached
 * production. Never compare `getDay()` against `weekDay` directly; go
 * through `isWorkdayEnabledOnDate` below, which takes a `Date` so the raw
 * weekday number is never in reach of a caller.
 */
const toApiWeekDay = (date: Date): number => {
  const jsWeekDay = date.getDay();
  return jsWeekDay === 0 ? 7 : jsWeekDay;
};

// Whether `workdays` enables the weekday that `date` falls on. Takes a Date
// (not a weekday number) so the JS→API conversion cannot be skipped.
export function isWorkdayEnabledOnDate(
  workdays: BarberScheduleWorkday[],
  date: Date,
): boolean {
  const weekDay = toApiWeekDay(date);
  return workdays.some(
    (workday) => workday.weekDay === weekDay && workday.enabled,
  );
}

const isScheduleActiveOn = (
  schedule: BarberSchedule,
  dateKey: string,
): boolean => {
  const startsAfterDate =
    schedule.startDate && toApiDateKey(schedule.startDate) > dateKey;
  const endsBeforeDate =
    schedule.endDate && toApiDateKey(schedule.endDate) < dateKey;
  return !startsAfterDate && !endsBeforeDate;
};

// Precedence matches WOW's getIsDayEnabledFromBarber: an explicit whole-day
// override (off or on) always wins over the recurring weekly schedule.
export function isBarberWorkingOnDate(
  barber: BarberScheduleInfo | undefined,
  date: Date,
): boolean {
  if (!barber) return false;

  const dateKey = toDateKey(date);

  const isExplicitlyOff = (barber.days_off ?? []).some(
    (dayOff) => dayOff.wholeDay && toApiDateKey(dayOff.day) === dateKey,
  );
  if (isExplicitlyOff) return false;

  const isExplicitlyOn = (barber.days_on ?? []).some(
    (dayOn) => toApiDateKey(dayOn.day) === dateKey,
  );
  if (isExplicitlyOn) return true;

  return (barber.schedules ?? []).some(
    (schedule) =>
      isScheduleActiveOn(schedule, dateKey) &&
      isWorkdayEnabledOnDate(schedule.workdays, date),
  );
}

// Returns the subset of `dates` where at least one of the given barbers is
// confirmed off (per isBarberWorkingOnDate). `undefined` entries (e.g. "any
// barber" guests with no specific schedule info) never contribute a match.
export function getBarberOffDates(
  barbers: (BarberScheduleInfo | undefined)[],
  dates: Date[],
): Date[] {
  return dates.filter((date) =>
    barbers.some(
      (barber) => barber !== undefined && !isBarberWorkingOnDate(barber, date),
    ),
  );
}

const AFTER_HOURS_START_MINUTES = 20 * 60;

// A finish before the start means the shift runs past midnight; an equal
// start and finish carries no hours.
const workdayEndsAfterHours = (workday: BarberScheduleWorkday): boolean => {
  const start = workday.startHour * 60 + workday.startMinute;
  const finish = workday.finishHour * 60 + workday.finishMinute;
  return (
    finish !== start && (finish > AFTER_HOURS_START_MINUTES || finish < start)
  );
};

// Days-on overrides carry no hours, so only a recurring schedule can offer
// After hours.
export function dayHasAfterHours(
  barbers: (BarberScheduleInfo | undefined)[],
  date: Date,
): boolean {
  const dateKey = toDateKey(date);

  return barbers.some((barber) => {
    if (!barber) return false;

    const isWholeDayOff = (barber.days_off ?? []).some(
      (dayOff) => dayOff.wholeDay && toApiDateKey(dayOff.day) === dateKey,
    );
    if (isWholeDayOff) return false;

    return (barber.schedules ?? []).some(
      (schedule) =>
        isScheduleActiveOn(schedule, dateKey) &&
        schedule.workdays.some(
          (workday) =>
            isWorkdayEnabledOnDate([workday], date) &&
            workdayEndsAfterHours(workday),
        ),
    );
  });
}

export function isAnyProfessionalWorkingOnDate(
  barbers: (BarberScheduleInfo | undefined)[],
  date: Date,
): boolean {
  return barbers.some((barber) => isBarberWorkingOnDate(barber, date));
}
